<script setup lang="ts">
import type { NuxtError } from '#app';
import { useTranslation } from '~/composables/useTranslation';
const TsHeaderCheckout = defineAsyncComponent(() => import('~/components/TsHeaderCheckout.vue'));

type GenericErrorCause = 'PageNotFound' | 'SomethingWentWrong';

const config = useRuntimeConfig();

const { isMobile } = useDevice();

const { $customClientErrorHandler, $customClientWarningHandler } = useNuxtApp();

const props = defineProps({
  error: {
    type: Object as () => NuxtError,
    default: null,
  },
});

const handleError = () => clearError({ redirect: '/' });

// GTM Logger On Nuxt Error Boundary: EW-479
function logErrorBoundary(cause: GenericErrorCause, error?: NuxtError) {
  logEvent({
    options: {
      eventAction: 'error',
      eventProperties: error,
      eventInteraction: true,
      eventLabel: `Error Page - ${error?.statusCode} ${cause}`,
      eventName: 'error_messages',
    },
  });
}

onMounted(async () => {
  const previousRoute = sessionStorage.getItem('previousRoute');
  let errorMessage = 'Something went wrong';
  let errorMeta: Record<string, unknown> = { serverError: true };
  let errorCause: GenericErrorCause = 'SomethingWentWrong';

  if (props.error && props.error.statusCode === 404) {
    errorMeta = {
      pageNotFoundError: true,
      ReferralUrl: previousRoute || 'No Previous Route Record Found',
    };

    errorMessage = 'Error Page - 404 Page Not Found';
    errorCause = 'PageNotFound';
    logEvent({
      options: {
        eventName: 'error_state',
        eventAction: 'page_view',
        eventLabel: '404:page_not_found',
        eventInteraction: false,
        eventProperties: {
          error: {
            error_type: '404:page_not_found',
          },
        },
      },
    });
    $customClientWarningHandler('low', errorMessage, errorMeta);
    return;
  }

  $customClientErrorHandler(props.error, 'high', errorMessage, errorMeta);
  logErrorBoundary(errorCause, props.error);
});
</script>

<template>
  <div class="h-screen">
    <TsHeaderCheckout />
    <TsSection
      append-class="h-full grid place-content-center mb-0 px-0 bg-cover bg-no-repeat pb-7 pt-1.5"
    >
      <TsTypography align="center" variant="h1" append-class="text-[#065CAB] mb-0"
        >Oops!</TsTypography
      >

      <TsImage
        v-if="error && error.statusCode === 404"
        src="/images/error404.png"
        alt=""
        height="156"
        width="273"
        class="mx-auto object-cover"
        loading="lazy"
      />

      <TsImage
        v-else
        src="/images/500Error.png"
        alt="bannerImage"
        height="156"
        width="337"
        class="mx-auto object-cover"
        loading="lazy"
      />

      <TsTypography align="center" variant="h4" append-class="mb-5">
        {{
          config.public.appDebug
            ? error?.message
            : useTranslation('errorMessage1', 'Er is iets misgegaan.')
        }}
      </TsTypography>

      <TsTypography align="center" :append-class="isMobile ? 'mb-5 w-full' : 'mb-5 w-1/3 mx-auto'">
        {{
          useTranslation(
            'errorMessage2',
            'Onze excuses voor de hik. Ons team van technische tovenaars is gestuurd om de gremlins in het systeem te temmen'
          )
        }}
      </TsTypography>

      <TsMedia append-class="justify-center">
        <TsButton
          size="md"
          :label="useTranslation('continueShopping', 'Ga door met winkelen')"
          append-class="whitespace-nowrap px-9"
          @click="handleError"
        />
      </TsMedia>
    </TsSection>
  </div>
</template>
